import * as Sentry from '@sentry/react';
import {PaymentRequest, Stripe} from '@stripe/stripe-js';
import {PaymentType} from '../analytics/paywallEvents';
import {SupportedCountryCode} from './currency';
import {loadStripeWithRetry, markStripeNeeded} from './stripeLoader';

export type WalletCheck = {paymentRequest?: PaymentRequest; paymentTypes: PaymentType[]};

const localeCurrencyMapping: Record<SupportedCountryCode, string> = {
  US: 'usd',
  GB: 'gbp',
  CA: 'cad',
  AU: 'aud',
  DE: 'eur',
};

/**
 * Build the payment request and ask Stripe whether Apple or Google Pay is available. Returns the request only when
 * one is, plus the methods the user will be offered.
 */
export const runWalletCheck = async (
  stripe: Stripe,
  amount: number, //What will appear in the sheet, in USD cents
  label: string,
  locale: SupportedCountryCode,
): Promise<WalletCheck> => {
  const paymentRequest = stripe.paymentRequest({
    country: 'US', // country code of merchant
    currency: localeCurrencyMapping[locale],
    total: {label, amount},
    requestPayerEmail: true,
    requestPayerName: true,
  });
  const canMakePayment = await paymentRequest.canMakePayment();
  const {applePay, googlePay} = canMakePayment ?? {};
  if (applePay || googlePay) {
    const paymentTypes: PaymentType[] = [];
    if (applePay) {
      paymentTypes.push('apple');
    }
    if (googlePay) {
      paymentTypes.push('google');
    }
    return {paymentRequest, paymentTypes};
  }
  return {paymentTypes: ['card']};
};

let stripePromise: Promise<Stripe | null> | null = null;
type EarlyCheck = {stripe: Stripe; check: Promise<WalletCheck | null>; startedAt: number; endedAt?: number};
let early: EarlyCheck | null = null;

const now = () => (typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : 0);

/**
 * When the payment button's prerequisites were ready, in ms since page load, so `Payment Method Shown` can say what
 * the button waited for. Set by `loadWalletStripe` and `checkWallet`; see `getWalletTiming`.
 */
let stripeReadyAt: number | undefined;
let usedCheck: {startedAt: number; endedAt: number; early: boolean} | undefined;

export type WalletTiming = {
  /** ms since page load when Stripe.js had loaded and the instance existed. */
  stripeReadyMs?: number;
  /** How long Stripe's Apple/Google Pay check (`canMakePayment`) took for the check the page used. */
  walletCheckMs?: number;
  /** ms since page load when that check finished. */
  walletCheckDoneMs?: number;
  /** Whether the page used the check started at boot (`startWalletCheckEarly`). */
  walletCheckEarly?: boolean;
};

export const getWalletTiming = (): WalletTiming => ({
  ...(stripeReadyAt !== undefined ? {stripeReadyMs: Math.round(stripeReadyAt)} : {}),
  ...(usedCheck
    ? {
        walletCheckMs: Math.round(usedCheck.endedAt - usedCheck.startedAt),
        walletCheckDoneMs: Math.round(usedCheck.endedAt),
        walletCheckEarly: usedCheck.early,
      }
    : {}),
});

/**
 * One Stripe instance for the page, so a wallet check started early belongs to the instance the page renders with.
 * A checkout calling this marks its page for the Stripe load fallback; the boot-time prefetch passes `prefetch`.
 */
export const loadWalletStripe = ({prefetch = false}: {prefetch?: boolean} = {}): Promise<Stripe | null> => {
  if (!prefetch) {
    markStripeNeeded();
  }
  if (!stripePromise) {
    // Stamped when Stripe.js is ready, including after a retry, so a slow or failed-then-recovered load shows up.
    stripePromise = loadStripeWithRetry('walletPrefetch', {prefetch: true}).then(stripe => {
      if (stripe && stripeReadyAt === undefined) {
        stripeReadyAt = now();
      }
      return stripe;
    });
  }
  return stripePromise;
};

/**
 * Start loading Stripe and the wallet check at boot, while GrowthBook and the link-out redeem are still in flight,
 * instead of once the paywall mounts. The amount and currency are placeholders: `checkWallet` sets the real ones
 * before the page gets the request, and neither affects whether a wallet is available.
 */
export const startWalletCheckEarly = (): void => {
  loadWalletStripe({prefetch: true}).then(stripe => {
    if (stripe && !early) {
      const startedAt = now();
      const entry: EarlyCheck = {
        stripe,
        startedAt,
        check: runWalletCheck(stripe, 100, 'Rise', 'US')
          .then(result => {
            entry.endedAt = now();
            return result;
          })
          .catch(error => {
            Sentry.captureException(error);
            return null;
          }),
      };
      early = entry;
    }
  });
};

/** The wallet check for this page: the one started at boot when there is one for this instance, else a new one. */
export const checkWallet = async (
  stripe: Stripe,
  amount: number,
  label: string,
  locale: SupportedCountryCode,
): Promise<WalletCheck> => {
  if (early && early.stripe === stripe) {
    const entry = early;
    early = null;
    const result = await entry.check;
    if (result) {
      result.paymentRequest?.update({currency: localeCurrencyMapping[locale], total: {label, amount}});
      usedCheck = {startedAt: entry.startedAt, endedAt: entry.endedAt ?? now(), early: true};
      return result;
    }
  }
  const startedAt = now();
  const result = await runWalletCheck(stripe, amount, label, locale);
  usedCheck = {startedAt, endedAt: now(), early: false};
  return result;
};
